/**
 * ThemeStore — device theme (light/dark, persisted to localStorage 'sg.theme')
 * plus the validated per-organization primary (02-design-system.md §2).
 *
 * NOT wired into RootStore here; the integration lead registers it after
 * acceptance (V1-01 packet). `applyTheme()` below is the boot entry.
 */
import { makeAutoObservable } from 'mobx';
import { DEFAULT_PRIMARY, INK_HEX } from './contrast';
import {
  applyPrimary,
  detectColorMixSupport,
  type AppliedPrimary,
  type ApplyPrimaryOptions,
} from './applyTheme';

export const THEME_STORAGE_KEY = 'sg.theme';

type ThemeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface ThemeStoreOptions {
  /** Injectable for tests; defaults to the real document. */
  documentElement?: HTMLElement | null;
  storage?: ThemeStorage | null;
  supportsColorMix?: () => boolean;
  warn?: (message: string) => void;
}

function defaultRoot(): HTMLElement | null {
  return typeof document !== 'undefined' ? document.documentElement : null;
}

function defaultStorage(): ThemeStorage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null; // storage access can throw in privacy modes
  }
}

export class ThemeStore {
  /** Dark mode flag; the pre-paint script in index.html seeds the class. */
  isDark = false;

  /** Last value passed to setPrimary (normalized), null = default brand. */
  requestedPrimary: string | null = null;
  /** Effective primary actually applied to the document. */
  appliedPrimary: string = DEFAULT_PRIMARY;
  appliedForeground: string = INK_HEX;
  /** Set when the requested org color was rejected and the brand fallback applied. */
  primaryFallback: AppliedPrimary['fallback'] = null;

  private readonly root: HTMLElement | null;
  private readonly storage: ThemeStorage | null;
  private readonly applyOptions: ApplyPrimaryOptions;

  constructor(options: ThemeStoreOptions = {}) {
    this.root = options.documentElement !== undefined ? options.documentElement : defaultRoot();
    this.storage = options.storage !== undefined ? options.storage : defaultStorage();
    this.applyOptions = {
      documentElement: this.root,
      supportsColorMix: options.supportsColorMix ?? detectColorMixSupport,
      ...(options.warn ? { warn: options.warn } : {}),
    };
    // Seed from the pre-paint class so first render agrees with the document.
    this.isDark = this.root?.classList.contains('dark') ?? false;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setDark(isDark: boolean): void {
    this.isDark = isDark;
    this.root?.classList.toggle('dark', isDark);
    try {
      this.storage?.setItem(THEME_STORAGE_KEY, isDark ? 'dark' : 'light');
    } catch {
      // Persistence is best-effort (private browsing, quota).
    }
  }

  toggleDark(): void {
    this.setDark(!this.isDark);
  }

  /**
   * Applies a validated org primary; `null` restores the default brand.
   * Rejected colors keep the default brand and record the fallback reason.
   */
  setPrimary(hex: string | null): void {
    const applied = applyPrimary(hex, this.applyOptions);
    this.requestedPrimary = applied.fallback
      ? applied.fallback.requested
      : hex
        ? applied.hex
        : null;
    this.appliedPrimary = applied.hex;
    this.appliedForeground = applied.foreground;
    this.primaryFallback = applied.fallback;
  }
}

let defaultStore: ThemeStore | null = null;

/**
 * Boot entry: creates (once) the default ThemeStore, synchronizes the .dark
 * class with the store, and returns it. The integration lead can register the
 * same instance on RootStore, or construct its own and pass it to the shells.
 */
export function applyTheme(options?: ThemeStoreOptions): ThemeStore {
  if (!defaultStore) defaultStore = new ThemeStore(options);
  return defaultStore;
}

/** Shared instance used by shells when no store prop is supplied. */
export function getDefaultThemeStore(): ThemeStore {
  return applyTheme();
}

/** Test-only: drop the singleton so each test starts clean. */
export function resetDefaultThemeStore(): void {
  defaultStore = null;
}
